import React, { useState, useEffect } from 'react';
import { BASE_URL } from '../../config/constants';
import { generateToken, TOKEN_HEADER } from '../../utils/tokenUtils';
import './Settings.css';

function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('profile');
  
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  const [professionalData, setProfessionalData] = useState({
    title: '',
    company: '',
    techStack: '',
    currentRole: ''
  });

  const [selectedTechnologies, setSelectedTechnologies] = useState([]);

  const availableTechnologies = [
    'JavaScript', 'Python', 'Java', 'C#', 'TypeScript', 'React', 'Angular', 
    'Vue.js', 'Node.js', 'Express', 'Django', 'Flask', 'Spring Boot', 
    '.NET', 'SQL', 'MongoDB', 'PostgreSQL', 'AWS', 'Azure', 'GCP', 
    'Docker', 'Kubernetes', 'DevOps', 'CI/CD', 'Machine Learning', 
    'Data Science', 'AI', 'Blockchain', 'Mobile Development', 'iOS', 
    'Android', 'React Native', 'Flutter', 'GraphQL', 'REST API', 
    'Microservices', 'Cybersecurity', 'Cloud Computing'
  ];

  useEffect(() => {
    fetchUserSettings();
  }, []);

  const fetchUserSettings = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId');
      const userEmail = localStorage.getItem('userEmail');
      const userName = localStorage.getItem('userName');

      // Parse name into first and last
      const nameParts = userName ? userName.split(' ') : ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Set basic profile data from localStorage
      setProfileData({
        firstName,
        lastName,
        email: userEmail || ''
      });

      // Fetch additional user settings from API if available
      if (userId) {
        try {
          const response = await fetch(`${BASE_URL}/users/${userId}/settings`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              [TOKEN_HEADER]: generateToken(),
            }
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              const settings = result.data;
              
              if (settings.professional) {
                setProfessionalData({
                  title: settings.professional.title || '',
                  company: settings.professional.company || '',
                  techStack: settings.professional.techStack || '',
                  currentRole: settings.professional.currentRole || ''
                });
              }

              if (settings.technologies) {
                setSelectedTechnologies(settings.technologies || []);
              }
            }
          }
        } catch (error) {
          console.log('Settings not found, using defaults');
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfessionalChange = (e) => {
    const { name, value } = e.target;
    setProfessionalData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleTechnology = (tech) => {
    setSelectedTechnologies(prev => {
      if (prev.includes(tech)) {
        return prev.filter(t => t !== tech);
      } else {
        return [...prev, tech];
      }
    });
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const userId = localStorage.getItem('userId');
      
      // Update localStorage
      const fullName = `${profileData.firstName} ${profileData.lastName}`.trim();
      localStorage.setItem('userName', fullName);
      localStorage.setItem('userEmail', profileData.email);

      // Save to API
      const response = await fetch(`${BASE_URL}/users/${userId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        },
        body: JSON.stringify({
          profile: profileData,
          professional: professionalData,
          technologies: selectedTechnologies
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'An error occurred while saving settings.' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="spinner-large"></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>⚙️ Settings</h1>
        <p>Manage your profile and preferences</p>
      </div>

      {message.text && (
        <div className={`settings-message ${message.type}`}>
          <span>{message.type === 'success' ? '✓' : '⚠️'}</span>
          {message.text}
        </div>
      )}

      <div className="settings-tabs">
        <button 
          className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          👤 Profile
        </button>
        <button 
          className={`tab-button ${activeTab === 'professional' ? 'active' : ''}`}
          onClick={() => setActiveTab('professional')}
        >
          💼 Professional
        </button>
        <button 
          className={`tab-button ${activeTab === 'technologies' ? 'active' : ''}`}
          onClick={() => setActiveTab('technologies')}
        >
          💻 Technologies
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'profile' && (
          <div className="settings-section">
            <h2>Basic Profile Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={profileData.firstName}
                  onChange={handleProfileChange}
                  placeholder="Enter your first name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={profileData.lastName}
                  onChange={handleProfileChange}
                  placeholder="Enter your last name"
                  required
                />
              </div>
              <div className="form-group full-width">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  placeholder="Enter your email address"
                  required
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'professional' && (
          <div className="settings-section">
            <h2>Professional Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="title">Job Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={professionalData.title}
                  onChange={handleProfessionalChange}
                  placeholder="e.g., Software Engineer"
                />
              </div>
              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={professionalData.company}
                  onChange={handleProfessionalChange}
                  placeholder="Enter your company name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="currentRole">Current Role</label>
                <select
                  id="currentRole"
                  name="currentRole"
                  value={professionalData.currentRole}
                  onChange={handleProfessionalChange}
                >
                  <option value="">Select a role</option>
                  <option value="developer">Developer</option>
                  <option value="designer">Designer</option>
                  <option value="manager">Manager</option>
                  <option value="architect">Architect</option>
                  <option value="analyst">Analyst</option>
                  <option value="engineer">Engineer</option>
                  <option value="consultant">Consultant</option>
                  <option value="student">Student</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label htmlFor="techStack">Tech Stack</label>
                <textarea
                  id="techStack"
                  name="techStack"
                  value={professionalData.techStack}
                  onChange={handleProfessionalChange}
                  placeholder="Describe your current tech stack (e.g., React, Node.js, AWS)"
                  rows="3"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'technologies' && (
          <div className="settings-section">
            <h2>Technologies of Interest</h2>
            <p className="section-description">
              Select the technologies you're interested in learning or working with
            </p>
            <div className="technologies-grid">
              {availableTechnologies.map((tech) => (
                <button
                  key={tech}
                  className={`tech-chip ${selectedTechnologies.includes(tech) ? 'selected' : ''}`}
                  onClick={() => toggleTechnology(tech)}
                >
                  {selectedTechnologies.includes(tech) && <span className="check-icon">✓</span>}
                  {tech}
                </button>
              ))}
            </div>
            <div className="selected-count">
              {selectedTechnologies.length} {selectedTechnologies.length === 1 ? 'technology' : 'technologies'} selected
            </div>
          </div>
        )}

        <div className="settings-actions">
          <button 
            className="save-button"
            onClick={handleSaveProfile}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-small"></span>
                Saving...
              </>
            ) : (
              <>
                💾 Save Changes
              </>
            )}
          </button>
          <button 
            className="cancel-button"
            onClick={fetchUserSettings}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default Settings;
